/**
 * Checkout Page - Dedicated page for mobile checkout
 * Access at: /checkout-channel-invite
 *
 * Hotfix 2026-06: NO crea PaymentIntent al montar — primero captura email,
 * luego PI solo al continuar (reduce PIs fantasma en Stripe).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import {
  STRIPE_PUBLISHABLE_KEY,
  STRIPE_APPEARANCE,
  CREATE_PAYMENT_INTENT_ENDPOINT,
  getPackPricing as getPackPricingStatic,
} from "@/config/stripe";
import { getMercadoPagoArMembershipUrl } from "@/lib/mercadopagoArCheckoutUrl";
import { usePackConfig } from "@/lib/usePackConfig";
import { Loader2 } from "lucide-react";
import {
  computeStripePackCheckoutAmounts,
  getOrCreateSessionWatcherLuckPercent,
  getWatcherLuckDiscountMaxPercentClient,
} from "@/lib/watcherLuckDiscount";
import {
  getCheckoutAttributionMetadataPostHog,
  trackStripePayFailurePostHog,
} from "@/lib/posthogAnalytics";
import { postStripeCheckoutAbandonmentCapture } from "@/lib/stripeCheckoutRecovery";
import { isPremiumInviteMembershipCheckout } from "@/lib/premiumVipCheckoutDestination";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { PREMIUM_INVITE_FLOW_NAME } from "@/config/bioTemplatePremiumFlow";
import { PREMIUM_INVITE_LANDING_PATH } from "@/lib/premiumVipCheckoutDestination";

const GIFT_PACKS = new Set(["birthday_gift", "sorpresa"]);

let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

type CheckoutPhase = "email" | "payment";

const emailGateCopy = {
  es: {
    title: "Tu acceso exclusivo",
    subtitle: "Ingresá tu email para continuar al pago seguro",
    emailLabel: "Email",
    emailPlaceholder: "tu@email.com",
    emailInvalid: "Ingresá un email válido",
    continue: "Continuar al pago",
    back: "Volver",
    secure: "Usamos tu email solo para el recibo y recovery si no completás el pago",
  },
  en: {
    title: "Your exclusive access",
    subtitle: "Enter your email to continue to secure checkout",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    emailInvalid: "Please enter a valid email",
    continue: "Continue to payment",
    back: "Back",
    secure: "We only use your email for your receipt and recovery if you don't complete payment",
  },
} as const;

function isValidEmail(email: string): boolean {
  const v = email.trim();
  return v.length >= 5 && v.includes("@") && v.includes(".");
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const packId = searchParams.get("pack") || "channel_invite";
  const countryCode = searchParams.get("country") || "US";
  const language = (searchParams.get("lang") || "es") as "en" | "es";
  const isPremiumInviteChannel = isPremiumInviteMembershipCheckout(searchParams);
  const checkoutFlowName = isPremiumInviteChannel
    ? PREMIUM_INVITE_FLOW_NAME
    : packId === "channel_invite"
      ? "channel_invite"
      : "stripe_checkout";
  const checkoutLandingPath = isPremiumInviteChannel
    ? searchParams.get("landing")?.trim() || PREMIUM_INVITE_LANDING_PATH
    : window.location.pathname;

  const [phase, setPhase] = useState<CheckoutPhase>("email");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isCreatingPi, setIsCreatingPi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkoutStripeUnavailableTrackedRef = useRef(false);
  const emailCapturedRef = useRef(false);

  const copy = emailGateCopy[language];
  const { getPackPricing } = usePackConfig();
  const packPricing =
    (["sorpresa", "exclusive"].includes(packId)
      ? getPackPricingStatic(packId, countryCode)
      : null) ?? getPackPricing(packId, countryCode);

  const checkoutWatcherLuckEligible =
    getWatcherLuckDiscountMaxPercentClient() > 0 &&
    !(countryCode.toUpperCase() === "AR" && packId === "channel_invite");

  const checkoutWatcherLuckPercent = checkoutWatcherLuckEligible
    ? getOrCreateSessionWatcherLuckPercent()
    : 0;

  const checkoutAmounts =
    packPricing != null
      ? computeStripePackCheckoutAmounts({
          baseAmountCents: packPricing.amount,
          packId,
          watcherLuckPercent: checkoutWatcherLuckPercent,
        })
      : null;

  const captureEmail = useCallback(
    (email: string, piId?: string) => {
      if (!packPricing || GIFT_PACKS.has(packId)) return;
      postStripeCheckoutAbandonmentCapture({
        email,
        packId,
        country: countryCode,
        language,
        amountCents: checkoutAmounts?.finalAmountCents ?? packPricing.amount,
        currency: "usd",
        paymentIntentId: piId,
        landingPath: checkoutLandingPath,
        flowName: checkoutFlowName,
        productName: language === "es" ? packPricing.nameEs : packPricing.name,
      });
      if (
        typeof (
          window as unknown as {
            posthogSafeCapture?: (e: string, p?: Record<string, unknown>) => void;
          }
        ).posthogSafeCapture === "function"
      ) {
        (
          window as unknown as {
            posthogSafeCapture: (e: string, p?: Record<string, unknown>) => void;
          }
        ).posthogSafeCapture("checkout_email_captured", {
          pack_id: packId,
          flow_surface: "checkout_channel_invite_page",
          has_payment_intent: Boolean(piId),
          country: countryCode,
        });
      }
    },
    [packId, packPricing, countryCode, language, checkoutAmounts, checkoutFlowName, checkoutLandingPath],
  );

  useEffect(() => {
    if (!isPremiumInviteChannel) return;
    const clerkEmail = user?.primaryEmailAddress?.emailAddress;
    void startVipMembershipVideoCheckout({
      countryCode,
      pathname: checkoutLandingPath,
      languageForPlans: localeForPlansFromMuxLanguage(language === "es" ? "es" : "en"),
      email: clerkEmail || undefined,
    });
  }, [isPremiumInviteChannel, countryCode, checkoutLandingPath, language, user]);

  const createPaymentIntent = async (email: string) => {
    if (!packPricing) return;
    if (packId === "channel_invite" && countryCode.toUpperCase() === "AR") {
      const mp = getMercadoPagoArMembershipUrl();
      if (mp) {
        window.location.href = mp;
        return;
      }
      trackStripePayFailurePostHog({
        stage: "flow_blocked",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "mp_ar_membership_missing_url",
        pack_id: packId,
        error_message: "Checkout MercadoPago para Argentina no configurado",
      });
      setError(
        language === "es"
          ? "Checkout para Argentina no disponible. Contactá a soporte."
          : "Checkout for Argentina is not available. Please contact support.",
      );
      return;
    }

    setError(null);
    setIsCreatingPi(true);
    let trackedFailure = false;
    try {
      const apiUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}`
          : CREATE_PAYMENT_INTENT_ENDPOINT;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: packPricing.amount,
          currency: "usd",
          packId: packId,
          buyerEmail: email,
          source: window.location.pathname,
          country: countryCode || undefined,
          watcherLuckDiscountPercent: checkoutWatcherLuckEligible ? checkoutWatcherLuckPercent : 0,
          metadata: {
            // Atribución de origen (landing/template/entry/referrer/UTM) →
            // Slack sale_confirmed + emails, para todos los packs.
            ...getCheckoutAttributionMetadataPostHog(),
            ...(packId === "channel_invite"
              ? isPremiumInviteChannel
                ? {
                    flow: PREMIUM_INVITE_FLOW_NAME,
                    flow_name: PREMIUM_INVITE_FLOW_NAME,
                    plan_tier: "vip",
                    product_type: "membership",
                    landing_path: checkoutLandingPath,
                  }
                : { flow_name: "channel_invite", product_type: "membership" }
              : {}),
          },
        }),
      });
      const text = await response.text();
      if (!response.ok) {
        let errMsg = "Failed to create payment intent";
        let stripeCode: string | undefined;
        try {
          const errorData = JSON.parse(text) as {
            error?: string;
            message?: string;
            code?: string;
            redirect_url?: string;
          };
          errMsg = errorData.error || errorData.message || errMsg;
          stripeCode = errorData.code;
          if (errorData.code === "WRONG_CHECKOUT_SURFACE" && errorData.redirect_url) {
            window.location.href = errorData.redirect_url;
            return;
          }
        } catch {
          if (response.status === 404)
            errMsg =
              language === "es"
                ? "No se encontró el servicio de pago."
                : "Payment service not found.";
          else if (response.status >= 500)
            errMsg =
              language === "es"
                ? "Error del servidor. Intenta de nuevo."
                : "Server error. Please try again.";
        }
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          http_status: response.status,
          stripe_error_code: stripeCode,
          error_message: errMsg,
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(errMsg);
      }
      let data: { clientSecret?: string; paymentIntentId?: string };
      try {
        data = JSON.parse(text) as { clientSecret?: string; paymentIntentId?: string };
      } catch {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: "invalid_json_response",
          http_status: response.status,
          error_message: "Invalid JSON from payment API",
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(
          language === "es" ? "Respuesta inválida del servidor." : "Invalid server response.",
        );
      }
      if (!data?.clientSecret) {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: "missing_client_secret",
          error_message: "Missing clientSecret in API response",
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
        trackedFailure = true;
        throw new Error(
          language === "es" ? "No se pudo inicializar el checkout." : "Could not initialize checkout.",
        );
      }
      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId ?? null);
      captureEmail(email, data.paymentIntentId);
      setPhase("payment");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to initialize checkout";
      if (!trackedFailure) {
        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "checkout_channel_invite_page",
          failure_kind: err instanceof TypeError ? "network_error" : "unknown",
          error_message: message,
          pack_id: packId,
          amount_cents: packPricing.amount,
          currency: "usd",
        });
      }
      setError(message);
    } finally {
      setIsCreatingPi(false);
    }
  };

  useEffect(() => {
    if (isPremiumInviteChannel) {
      setIsBootstrapping(true);
      return;
    }
    if (GIFT_PACKS.has(packId)) {
      window.location.replace(packId === "birthday_gift" ? "/r/cumple" : "/sorprendeme");
      return;
    }
    if (packId === "channel_invite" && countryCode.toUpperCase() === "AR") {
      const mp = getMercadoPagoArMembershipUrl();
      if (mp) {
        window.location.href = mp;
        return;
      }
      trackStripePayFailurePostHog({
        stage: "flow_blocked",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "mp_ar_membership_missing_url",
        pack_id: packId,
        error_message: "Checkout MercadoPago para Argentina no configurado",
      });
      setError(
        language === "es"
          ? "Checkout para Argentina no disponible. Contactá a soporte."
          : "Checkout for Argentina is not available. Please contact support.",
      );
      setIsBootstrapping(false);
      return;
    }
    if (!STRIPE_PUBLISHABLE_KEY) {
      trackStripePayFailurePostHog({
        stage: "invalid_config",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "missing_publishable_key",
        error_message: "Stripe not configured",
        pack_id: packId,
      });
      setError("Stripe not configured");
      setIsBootstrapping(false);
      return;
    }
    if (!packPricing) {
      trackStripePayFailurePostHog({
        stage: "invalid_config",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "invalid_pack_pricing",
        error_message: language === "es" ? "Producto no válido" : "Invalid product",
        pack_id: packId,
      });
      setError(language === "es" ? "Producto no válido" : "Invalid product");
      setIsBootstrapping(false);
      return;
    }
    setIsBootstrapping(false);
  }, [packId, countryCode, language, packPricing]);

  useEffect(() => {
    const onLeave = () => {
      if (!isValidEmail(buyerEmail) || GIFT_PACKS.has(packId)) return;
      captureEmail(buyerEmail, paymentIntentId ?? undefined);
    };
    window.addEventListener("pagehide", onLeave);
    return () => window.removeEventListener("pagehide", onLeave);
  }, [buyerEmail, paymentIntentId, packId, captureEmail]);

  const handleEmailContinue = async () => {
    if (!isValidEmail(buyerEmail)) {
      setEmailError(copy.emailInvalid);
      return;
    }
    setEmailError(null);
    if (!emailCapturedRef.current) {
      emailCapturedRef.current = true;
      captureEmail(buyerEmail);
    }
    await createPaymentIntent(buyerEmail);
  };

  const handleSuccess = (piId?: string, paymentIntentSecret?: string) => {
    const thankYouPath = language === "es" ? "/gracias" : "/thank-you";
    const params = new URLSearchParams();
    if (piId) params.append("payment_intent", piId);
    if (paymentIntentSecret) params.append("payment_intent_client_secret", paymentIntentSecret);
    window.location.href = `${thankYouPath}?${params.toString()}`;
  };

  const handleBack = () => {
    if (phase === "payment") {
      setPhase("email");
      setClientSecret(null);
      setPaymentIntentId(null);
      setError(null);
      return;
    }
    navigate(-1);
  };

  if (isBootstrapping) {
    const loadingText = language === "es" ? "Cargando…" : "Loading…";
    return (
      <div
        className="min-h-screen bg-black flex items-center justify-center p-4 font-body"
        aria-busy="true"
      >
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" aria-hidden />
          <p className="text-white/60 text-sm font-medium">{loadingText}</p>
        </div>
      </div>
    );
  }

  if (error && phase === "email") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 font-body" role="alert">
        <div className="max-w-md w-full bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center space-y-4">
          <h2 className="text-xl font-bold text-white font-display">
            {language === "es" ? "Error" : "Error"}
          </h2>
          <p className="text-white/60 text-sm font-medium">{error}</p>
          <button
            type="button"
            onClick={handleBack}
            className="w-full bg-white/10 text-white font-bold py-3 px-6 rounded-xl"
          >
            {copy.back}
          </button>
        </div>
      </div>
    );
  }

  if (phase === "email" && packPricing) {
    return (
      <div className="min-h-screen bg-black flex flex-col p-4 font-body text-white">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold font-display">{copy.title}</h1>
              <p className="text-white/60 text-sm">{copy.subtitle}</p>
              <p className="text-white/80 text-sm font-medium">
                {language === "es" ? packPricing.nameEs : packPricing.name}
              </p>
            </div>
            <label className="block space-y-2">
              <span className="text-sm text-white/70">{copy.emailLabel}</span>
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                value={buyerEmail}
                onChange={(e) => {
                  setBuyerEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onBlur={() => {
                  if (isValidEmail(buyerEmail) && !emailCapturedRef.current) {
                    emailCapturedRef.current = true;
                    captureEmail(buyerEmail);
                  }
                }}
                placeholder={copy.emailPlaceholder}
                className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              {emailError ? (
                <p className="text-red-400 text-xs">{emailError}</p>
              ) : null}
            </label>
            <p className="text-[11px] text-white/40 text-center">{copy.secure}</p>
            {error ? <p className="text-red-400 text-sm text-center">{error}</p> : null}
            <button
              type="button"
              disabled={isCreatingPi}
              onClick={() => void handleEmailContinue()}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold py-3.5 px-6 rounded-xl disabled:opacity-60 min-h-[48px]"
            >
              {isCreatingPi ? (
                <span className="inline-flex items-center gap-2 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {language === "es" ? "Preparando pago…" : "Preparing payment…"}
                </span>
              ) : (
                copy.continue
              )}
            </button>
            <button
              type="button"
              onClick={handleBack}
              className="w-full text-white/60 text-sm py-2"
            >
              {copy.back}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !packPricing || !clientSecret) {
    const errorTitle = language === "es" ? "Error en el pago" : "Payment error";
    const errorMsg =
      error ||
      (language === "es" ? "No se pudo inicializar el checkout" : "Could not initialize checkout");
    const retryLabel = language === "es" ? "Reintentar" : "Try again";
    const backLabel = language === "es" ? "Volver" : "Go back";
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 font-body" role="alert">
        <div className="max-w-md w-full bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center space-y-4">
          <h2 className="text-xl font-bold text-white font-display">{errorTitle}</h2>
          <p className="text-white/60 text-sm font-medium">{errorMsg}</p>
          <div className="flex flex-col gap-3 pt-2">
            {packPricing && isValidEmail(buyerEmail) && (
              <button
                type="button"
                onClick={() => void createPaymentIntent(buyerEmail)}
                className="w-full bg-white text-black font-bold py-3 px-6 rounded-xl min-h-[44px]"
              >
                {retryLabel}
              </button>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="w-full bg-white/10 text-white font-bold py-3 px-6 rounded-xl min-h-[44px]"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stripe = getStripe();
  if (!stripe) {
    if (!checkoutStripeUnavailableTrackedRef.current) {
      checkoutStripeUnavailableTrackedRef.current = true;
      trackStripePayFailurePostHog({
        stage: "stripe_js_unavailable",
        flow_surface: "checkout_channel_invite_page",
        failure_kind: "stripe_promise_null",
        pack_id: packId,
      });
    }
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/60">Stripe not configured</p>
      </div>
    );
  }

  return (
    <div
      className="h-dvh flex flex-col bg-black font-body"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Elements
        stripe={stripe}
        options={{
          clientSecret,
          appearance: STRIPE_APPEARANCE,
          locale: language === "es" ? "es" : "en",
        }}
      >
        <div className="flex-1 flex flex-col min-h-0">
          <StripeCheckoutForm
            amount={checkoutAmounts?.finalAmountCents ?? packPricing.amount}
            currency="usd"
            packName={language === "es" ? packPricing.nameEs : packPricing.name}
            packThumbnail={packPricing.thumbnail}
            {...(checkoutAmounts?.showDiscountBadge && checkoutAmounts.discountPercentDisplay != null
              ? {
                  originalAmount: checkoutAmounts.originalListAmountCents,
                  discountPercent: checkoutAmounts.discountPercentDisplay,
                }
              : {})}
            language={language}
            initialEmail={buyerEmail}
            onEmailChange={(email) => {
              if (isValidEmail(email)) captureEmail(email, paymentIntentId ?? undefined);
            }}
            onSuccess={handleSuccess}
            onError={setError}
            onBack={handleBack}
            flowSurface="checkout_channel_invite_page"
          />
        </div>
      </Elements>
    </div>
  );
}